import { Link } from "react-router-dom";
import { T } from "@/components/T";
import { useLang } from "@/context/LanguageContext";
import { resolveNav, PATRIKA, PANCHANGA, PANCHANGA_PREMIUM, ABOUT_LINKS, ACCOUNT_LINKS } from "@/lib/nav";
import { useSettings } from "@/context/SettingsContext";
import { Divider } from "@/components/MotifBackground";
import { Instagram, Youtube, Facebook } from "lucide-react";
import { WhatsAppIcon, TelegramIcon, XIcon } from "@/components/BrandIcons";

const SOCIAL_META = [
  { k: "twitter", Icon: XIcon, label: "X" },
  { k: "instagram", Icon: Instagram, label: "Instagram" },
  { k: "youtube", Icon: Youtube, label: "YouTube" },
  { k: "facebook", Icon: Facebook, label: "Facebook" },
  { k: "telegram", Icon: TelegramIcon, label: "Telegram" },
  { k: "whatsapp", Icon: WhatsAppIcon, label: "WhatsApp" },
];

export default function Footer() {
  const { pick } = useLang();
  const { isVisible, nav_config, socials, hub_sections } = useSettings();
  const socialLinks = SOCIAL_META.filter((s) => socials?.[s.k]);
  const DEFAULT_HUB_KEYS = new Set(["scriptures", "rishis", "concepts", "pilgrimage", "samskara", "sanskrit"]);
  const customByGroup = {};
  (hub_sections || []).filter((s) => s && s.key && !DEFAULT_HUB_KEYS.has(s.key)).forEach((s) => {
    const grp = s.group || "explore";
    if (!customByGroup[grp]) customByGroup[grp] = [];
    customByGroup[grp].push({ to: `/hub/${s.key}`, deva: s.label || s.key, en: s.label || s.key });
  });

  const groupCols = resolveNav(nav_config?.footer)
    .map((g) => ({ ...g, items: g.items.filter((l) => (l.to === "/explore" || isVisible(l.to)) && !l.hideNav) }))
    .map((g) => (customByGroup[g.key] ? { ...g, items: [...g.items, ...customByGroup[g.key]] } : g))
    .map((g) => ({ label: g.label, links: g.items }))
    .filter((c) => c.links.length > 0);

  const cols = [
    ...groupCols,
    { label: { deva: "पंचांग", en: "Panchangam" }, links: [PANCHANGA, PANCHANGA_PREMIUM] },
    { label: { deva: "पत्रिका", en: "Patrika" }, links: [PATRIKA] },
    { label: { deva: "खाता", en: "Account" }, links: [...ACCOUNT_LINKS, { to: "/search", deva: "खोज", en: "Search" }] },
  ];

  return (
    <footer className="relative z-[2] border-t border-line bg-[var(--surface)]">
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3">
            <p className="font-deva text-4xl text-ink">धर्मार्थ</p>
            <p className="mt-3 font-deva-body text-lg text-saffron">धर्मो रक्षति रक्षितः</p>
            <p className="mt-4 text-sm text-ink-3"><T>Reverence · Knowledge · Tradition · Accessibility</T></p>
            <p className="tg-footer mt-6 max-w-sm text-sm leading-relaxed text-ink-2">
              <T>Dharmartha is a digital initiative focused on making Bharatiya spiritual knowledge more accessible, organised and understandable.</T>
            </p>
            {socialLinks.length > 0 && (
              <div className="mt-6 flex gap-3" data-testid="footer-socials">
                {socialLinks.map(({ k, Icon, label: lbl }) => (
                  <a key={k} href={socials[k]} target="_blank" rel="noopener noreferrer" aria-label={lbl} data-testid={`social-${k}`} className="flex h-9 w-9 items-center justify-center border border-line text-ink-2 transition-colors hover:border-[var(--saffron)] hover:text-saffron">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 lg:grid-cols-4 md:col-span-9">
            {cols.map((col) => (
              <div key={col.label.en}>
                <p className="overline-label mb-4 text-ink-3">{pick(col.label.deva, col.label.en)}</p>
                <ul className="space-y-2.5">
                  {col.links.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} className="tg-footer link-underline text-sm text-ink-2 transition-colors hover:text-ink">
                        {pick(l.deva, l.en)}
                        {l.soon && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-gold/70"><T>soon</T></span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <Divider className="mt-16" />
        <div className="tg-footer mt-8 flex flex-col items-center justify-between gap-3 text-xs text-ink-3 sm:flex-row">
          <p>© {new Date().getFullYear()} <T>Dharmartha · A digital gateway to the knowledge traditions of Bharat.</T></p>
        </div>
        <p className="mt-3 text-center text-[11px] leading-relaxed text-ink-3" data-testid="footer-dpdp">
          <T>Compliant with The Digital Personal Data Protection Act, 2023 (DPDP Act). Your personal data — including birth details — is collected only with your consent, used solely to provide your services, never shared, and can be edited or permanently deleted by you at any time.</T>
        </p>
      </div>
    </footer>
  );
}
